import { useEffect, useState } from 'react';
import { TankSheet } from './TankSheet.jsx';
import { getDisplayUnits, formatVolume, displayTankSubtitle } from '../units.js';
import { OPEN_FEEDBACK_EVENT } from './Feedback.jsx';
import { tankPhotoUrl } from '../photo-url.js';
import { QuarantineBadge } from './QuarantineBadge.jsx';

// "Shared · can log" for a tank someone shared with this keeper.
export function sharedKicker(tank) {
  if (!tank || !tank.role || tank.role === 'owner') return null;
  if (tank.role === 'editor') return 'Shared · you can log';
  if (tank.role === 'viewer') return 'Shared · view only';
  return 'Shared';
}

export function Header({
  tank,
  tanks,
  onPickTank,
  onSignOut,
  onAddTank,
  onOpenSettings,
  onOpenAccount,
  onBackToOverview,
}) {
  const [open, setOpen] = useState(false);
  // While this header (and its feedback icon) is on screen, a phone hides
  // the floating feedback button (theme.css).
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('hdr-has-feedback');
    return () => root.classList.remove('hdr-has-feedback');
  }, []);
  const photoUrl = tank?.has_photo ? tankPhotoUrl(tank) : null;
  return (
    <header
      style={{
        padding: 'max(24px, calc(env(safe-area-inset-top) + 12px)) 20px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          flex: 1,
          textAlign: 'left',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              objectFit: 'cover',
              flexShrink: 0,
              border: '1px solid var(--surface-border)',
            }}
          />
        ) : tank ? (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface)',
              border: '1px solid var(--surface-border)',
              flexShrink: 0,
              fontSize: 26.5,
            }}
          >
            {tank.emoji || '🌊'}
          </div>
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* A literal "Online" with a pulsing dot sat here and never
              changed, so a keeper with a display read it as "my device is
              online". The kicker now says only what is true: whose tank. */}
          {sharedKicker(tank) && (
            <div
              className="font-heading"
              style={{
                fontSize: 12,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--cyan)',
                marginBottom: 2,
              }}
            >
              {sharedKicker(tank)}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h1
              className="font-heading"
              style={{
                // Scales down on narrow phones so the tank name fits
                // instead of truncating to "REEF ..." at 390px.
                fontSize: 'clamp(17px, 5vw, 24px)',
                lineHeight: 1.1,
                fontWeight: 600,
                letterSpacing: '0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tank ? tank.name.toUpperCase() : 'TANKSENSEI'}
            </h1>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-dim)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          {tank?.subtitle && (
            <p
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <QuarantineBadge tank={tank} style={{ marginRight: 6 }} />
              {displayTankSubtitle(tank)}
            </p>
          )}
          {!tank?.subtitle && tank?.volume_gallons && (
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>
              <QuarantineBadge tank={tank} style={{ marginRight: 6 }} />
              {formatVolume(tank.volume_gallons, getDisplayUnits(tank).volume)}
              {tank.type ? ` · ${tank.type}` : ''}
            </p>
          )}
        </div>
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Feedback lives here on a phone: the floating button covered the
            right edge of every list (owner, 2026-10-21). Near You is in the
            bottom bar, light / dark in Account, and Tank settings on the
            Tank tab (the tank menu keeps its own link). */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent(OPEN_FEEDBACK_EVENT))}
          aria-label="Send feedback"
          title="Send feedback"
          className="hdr-gear hdr-feedback"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
        {onOpenAccount && (
          <button
            onClick={onOpenAccount}
            aria-label="Account settings"
            title="Account settings"
            className="hdr-gear"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>
        )}
      </div>
      {open && tanks && (
        <TankSheet
          tanks={tanks}
          currentId={tank?.id}
          onBackToOverview={
            onBackToOverview
              ? () => {
                  onBackToOverview();
                  setOpen(false);
                }
              : null
          }
          onSignOut={onSignOut}
          onOpenSettings={
            onOpenSettings
              ? (id) => {
                  // Switch tanks first so settings opens against the
                  // tank the user tapped. onPickTank is idempotent when
                  // the id matches the active tank.
                  if (id !== tank?.id) onPickTank(id);
                  onOpenSettings();
                  setOpen(false);
                }
              : undefined
          }
          onPick={(id) => {
            onPickTank(id);
            setOpen(false);
          }}
          onAdd={
            onAddTank
              ? () => {
                  onAddTank();
                  setOpen(false);
                }
              : null
          }
          onClose={() => setOpen(false)}
          onOpenAccount={
            onOpenAccount
              ? () => {
                  setOpen(false);
                  onOpenAccount();
                }
              : undefined
          }
        />
      )}
    </header>
  );
}
